import { __extends, __spreadArray } from 'tslib';
import * as React from 'react';
import { throttle } from 'throttle-debounce';
import { classNames, validatePropType } from 'uitk-react-utils';
import styles from './uitk-tabs.css';
import { UitkTabAnchor } from './uitk-tab-anchor.esm.js';
import { UitkTab } from './uitk-tab.esm.js';
import { UitkTabHighlighter } from './uitk-tab-highlighter.esm.js';

var getClassNameList = classNames.bind(styles);
var DEFAULT_TAB_HEIGHT = 48; // 3x default font height (16px)
var UitkTabsType;
(function (UitkTabsType) {
    UitkTabsType["EQUAL_WIDTH"] = "equal-width";
    UitkTabsType["NATURAL"] = "natural";
})(UitkTabsType || (UitkTabsType = {}));
var UitkTabsBackGroundColorType;
(function (UitkTabsBackGroundColorType) {
    UitkTabsBackGroundColorType["PRIMARY"] = "background-primary";
    UitkTabsBackGroundColorType["SECONDARY"] = "background-secondary";
})(UitkTabsBackGroundColorType || (UitkTabsBackGroundColorType = {}));
var UitkNavigationType;
(function (UitkNavigationType) {
    UitkNavigationType["DEFAULT"] = "default";
    UitkNavigationType["JUMPLINK"] = "jumplink";
})(UitkNavigationType || (UitkNavigationType = {}));
var UitkTabs = /** @class */ (function (_super) {
    __extends(UitkTabs, _super);
    function UitkTabs(props) {
        var _this = _super.call(this, props) || this;
        _this.throttleDelay = 100;
        _this.animationTimer = 275;
        _this.listenersAdded = false;
        _this.idsCollection = [];
        _this.scrollMouse = { drag: false, down: false, initialMousePos: 0, scrollLeft: 0 };
        _this.mouseDown = function (ev) {
            _this.scrollMouse.down = true;
            _this.scrollMouse.initialMousePos = ev.clientX;
            _this.scrollMouse.scrollLeft = ev.currentTarget.scrollLeft;
        };
        _this.mouseUp = function (ev) {
            _this.scrollMouse.down = false;
            // Calculating if the mouse drags accidentally with a range of 10 pixels both to left and right.
            var hasDragToRight = _this.scrollMouse.initialMousePos + 10 > ev.clientX;
            var hasDragToLeft = _this.scrollMouse.initialMousePos - 10 < ev.clientX;
            // If it drags, then use the timeout to prevent click, if not, disabling drag.
            if (!hasDragToRight || !hasDragToLeft) {
                // Timeout drag flag in order to prevent click event
                setTimeout(function () {
                    _this.scrollMouse.drag = false;
                }, 0);
            }
            else {
                _this.scrollMouse.drag = false;
            }
        };
        _this.mouseMove = function (ev) {
            // Call only while dragging (mouseDown + mouseMove = drag)
            if (_this.scrollMouse.down) {
                _this.scrollMouse.drag = true;
                _this.tabScroll(ev);
            }
        };
        _this.updateHighlighter = function () {
            _this.setState({
                isTransitioning: true
            });
            _this.transitionTimeout = setTimeout(function () {
                _this.setState({
                    isTransitioning: false
                });
            }, _this.animationTimer);
        };
        _this.state = {
            isTransitioning: false,
            mounted: false
        };
        _this.tabsRef = React.createRef();
        _this.handleClick = _this.handleClick.bind(_this);
        _this.centerTab = _this.centerTab.bind(_this);
        _this.handleFocus = _this.handleFocus.bind(_this);
        _this.handleBlur = _this.handleBlur.bind(_this);
        _this.onKeyUp = _this.onKeyUp.bind(_this);
        _this.onKeyDown = _this.onKeyDown.bind(_this);
        _this.setSelectedElementOffset = _this.setSelectedElementOffset.bind(_this);
        _this.setSelectedTab = _this.setSelectedTab.bind(_this);
        _this.observeTabs = _this.observeTabs.bind(_this);
        _this.unobserveTabs = _this.unobserveTabs.bind(_this);
        _this.updateHighlighter = throttle(_this.throttleDelay, false, _this.updateHighlighter.bind(_this), false);
        _this.tabSelected = _this.tabSelected.bind(_this);
        _this.mouseDown = _this.mouseDown.bind(_this);
        _this.mouseMove = _this.mouseMove.bind(_this);
        _this.mouseUp = _this.mouseUp.bind(_this);
        return _this;
    }
    UitkTabs.prototype.componentDidMount = function () {
        var _a, _b;
        var navigationType = this.props.navigationType;
        if (this.tabsRef) {
            this.updateHighlighter();
        }
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', this.updateHighlighter);
        }
        if (this.idsCollection.length > 0 && navigationType === UitkNavigationType.JUMPLINK) {
            var topOfIntersectionObserver = this.tabsRef.current ? (_b = (_a = this.tabsRef) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.offsetHeight : DEFAULT_TAB_HEIGHT;
            var threePercentOfRestOfPage = (window.innerHeight - topOfIntersectionObserver) * 0.03;
            var bottomOfIntersectionObserver = window.innerHeight - (topOfIntersectionObserver + threePercentOfRestOfPage);
            var options = {
                rootMargin: "-".concat(topOfIntersectionObserver, "px 0px -").concat(bottomOfIntersectionObserver, "px"),
                threshold: 0
            };
            this.intersectionObserver = new IntersectionObserver(this.setSelectedTab, options);
            this.observeTabs();
        }
        this.setState({
            mounted: true
        });
    };
    UitkTabs.prototype.componentDidUpdate = function (prevProps) {
        var selectedTab = this.props.selectedTab;
        if (selectedTab !== prevProps.selectedTab) {
            // When the activeIndex or the links are updated, transition
            // the highlighter to the active tab.
            this.updateHighlighter();
        }
    };
    UitkTabs.prototype.componentWillUnmount = function () {
        if (typeof window !== 'undefined') {
            window.removeEventListener('resize', this.updateHighlighter);
        }
        if (this.transitionTimeout) {
            clearTimeout(this.transitionTimeout);
            this.transitionTimeout = null;
        }
        if (this.listenersAdded) {
            document.body.removeEventListener('keyup', this.onKeyUp);
            document.body.removeEventListener('keydown', this.onKeyDown);
            this.listenersAdded = false;
        }
    };
    UitkTabs.prototype.handleBlur = function () {
        if (this.listenersAdded) {
            document.body.removeEventListener('keyup', this.onKeyUp);
            document.body.removeEventListener('keydown', this.onKeyDown);
            this.listenersAdded = false;
        }
    };
    UitkTabs.prototype.handleFocus = function (event) {
        if (!this.listenersAdded) {
            document.body.addEventListener('keyup', this.onKeyUp);
            document.body.addEventListener('keydown', this.onKeyDown);
            this.listenersAdded = true;
        }
        this.centerTab(event);
    };
    UitkTabs.prototype.handleClick = function (selectedTab, event, targetRef) {
        var _this = this;
        // Prevent click while dragging tabs
        if (this.scrollMouse.drag) {
            event.preventDefault();
            return;
        }
        var _a = this.props, navigationType = _a.navigationType, onTabClickCB = _a.onTabClickCB;
        this.tabSelected(selectedTab, event);
        if (typeof onTabClickCB === 'function') {
            onTabClickCB(selectedTab);
        }
        if (navigationType === UitkNavigationType.JUMPLINK && selectedTab >= 0) {
            if (targetRef && targetRef.current) {
                var _b = this.props, scrollOffset = _b.scrollOffset, scrollOffsetSelf = _b.scrollOffsetSelf;
                var scroll_1 = window.scrollY;
                var target = targetRef.current.getBoundingClientRect().top;
                var offset = (scrollOffset || 0) +
                    (scrollOffsetSelf && !!this.tabsRef.current ? this.tabsRef.current.getBoundingClientRect().height : 0);
                window.scrollTo({
                    behavior: 'smooth',
                    top: scroll_1 + target - offset
                });
                var firstBaseChild_1 = this.findBaseChild(targetRef.current);
                setTimeout(function () {
                    _this.observeTabs();
                    firstBaseChild_1.focus();
                }, 1000);
            }
        }
    };
    UitkTabs.prototype.onKeyDown = function (e) {
        var navigationType = this.props.navigationType;
        if (e.key === 'ArrowRight' ||
            e.key === 'ArrowLeft' ||
            (e.key === ' ' && navigationType !== UitkNavigationType.JUMPLINK)) {
            e.preventDefault();
        }
    };
    UitkTabs.prototype.onKeyUp = function (e) {
        var navigationType = this.props.navigationType;
        if (this.tabsRef.current) {
            var ul = this.tabsRef.current;
            var listItems = ul.children;
            var elemToFocus = void 0;
            var index = 0;
            if (e.key === 'ArrowRight') {
                index = this.selectedIndex + 1;
                elemToFocus = listItems[index];
            }
            else if (e.key === 'ArrowLeft') {
                index = this.selectedIndex - 1;
                elemToFocus = listItems[index];
            }
            else if (e.key === 'Tab') {
                var target = document.activeElement;
                for (var i = 0; i <= listItems.length; i++) {
                    if ((target === null || target === void 0 ? void 0 : target.parentElement) === listItems[i]) {
                        this.selectedIndex = i;
                    }
                }
                return;
            }
            else if (e.key === ' ') {
                if (navigationType !== UitkNavigationType.JUMPLINK) {
                    this.tabSelected(this.selectedIndex, e);
                }
            }
            if (elemToFocus === null || elemToFocus === void 0 ? void 0 : elemToFocus.classList.contains('uitk-tab')) {
                this.selectedIndex = index;
                elemToFocus.children[0].focus();
            }
        }
    };
    UitkTabs.prototype.getChildren = function () {
        var _this = this;
        var _a = this.props, children = _a.children, externalLinks = _a.externalLinks, navigationType = _a.navigationType, selectedTab = _a.selectedTab;
        var randomBase = "tab-".concat(Math.random().toString(36).substr(2, 9), "-");
        var navigationItems = [];
        var tabPaneItems = [];
        this.idsCollection = [];
        React.Children.forEach(children, function (child, index) {
            var _a = child.props, name = _a.name, className = _a.className, disabled = _a.disabled, id = _a.id, tabLinkAttr = _a.tabLinkAttr, targetRef = _a.targetRef, targetURI = _a.targetURI;
            var selected = index === selectedTab;
            var panelId = id || "".concat(randomBase).concat(index);
            if (selected) {
                _this.selectedIndex = index;
            }
            // TargetURI is what gets assigned to the href attribute of the anchor tag inside a UITK tab
            // So if our navigation type is jumplink and the child has a targetURI prop defined,
            // Push that targetURI's into idsCollection. The ID we push is the htmlId of the section
            // that we're jumping to: 'targetURI.slice(targetURI.indexOf('#'))'
            if (navigationType === UitkNavigationType.JUMPLINK && targetURI) {
                _this.idsCollection.push(targetURI.slice(targetURI.indexOf('#')));
            }
            navigationItems = __spreadArray(__spreadArray([], navigationItems, true), [
                React.createElement(UitkTabAnchor, { disabled: disabled, className: className, handleClick: _this.handleClick, handleFocus: _this.handleFocus, handleBlur: _this.handleBlur, key: index, index: index, name: name, selected: selected, targetRef: targetRef, targetURI: targetURI, panelId: panelId, externalLinks: externalLinks, navigationType: navigationType, tabLinkAttr: tabLinkAttr })
            ], false);
            // If the current child component does not have children of its own, leave tabPaneItems as is
            // Else, if it does, create a new tabPaneItems array with the old content as well as a new UITKTab
            tabPaneItems = child.props.children
                ? __spreadArray(__spreadArray([], tabPaneItems, true), [
                    React.createElement(UitkTab, { key: index, name: name, selected: selected, targetURI: targetURI, id: panelId }, child.props.children)
                ], false) : tabPaneItems;
        });
        // Return a 2d array where the first array is an array of UITKTabAnchors and the second is an array of
        // UITKTabs.
        return [navigationItems, tabPaneItems];
    };
    UitkTabs.prototype.setSelectedElementOffset = function (selectedTab) {
        this.selectedTabOffset = selectedTab.offsetLeft;
    };
    // This function is called whenever even one pixel of an observed item interesects the sliver of the viewport we are observing
    UitkTabs.prototype.setSelectedTab = function (entries) {
        var _this = this;
        var onTabSelect = this.props.onTabSelect;
        // This const is an array of section's id's that the tab will jump to
        var refs = this.idsCollection;
        var href = '';
        // For each section that has triggered a change, i.e. entered or left the sliver of the screen we're observing, do the following.
        entries.forEach(function (entry) {
            if (entry.isIntersecting) {
                href = "#".concat(entry.target.getAttribute('id'));
                _this.selectedIndex = refs.indexOf(href);
                // In the case of jumplink tabs, this callback would cause the highlighter to switch to the active tab
                if (typeof onTabSelect === 'function') {
                    onTabSelect(_this.selectedIndex);
                }
            }
        });
    };
    UitkTabs.prototype.tabSelected = function (selectedTab, event) {
        var onTabSelect = this.props.onTabSelect;
        this.selectedIndex = selectedTab;
        this.unobserveTabs();
        if (typeof onTabSelect === 'function') {
            onTabSelect(this.selectedIndex, event);
        }
        this.centerTab(event);
    };
    UitkTabs.prototype.findBaseChild = function (obj) {
        var elem = obj;
        while (elem.childElementCount > 0) {
            elem = elem.firstElementChild;
        }
        return elem;
    };
    // Observe each of the html element defined with a corresponding id in idsCollection.
    // This will allow intersectionObserver to determine whether or not they intersect with the
    // viewport. This method acts on the DOM.
    UitkTabs.prototype.observeTabs = function () {
        var _this = this;
        this.idsCollection.forEach(function (id) {
            var elem = document.querySelector(id);
            if (elem) {
                _this.intersectionObserver.observe(elem);
            }
        });
    };
    UitkTabs.prototype.unobserveTabs = function () {
        var _this = this;
        this.idsCollection.forEach(function (id) {
            var elem = document.querySelector(id);
            if (elem) {
                _this.intersectionObserver.unobserve(elem);
            }
        });
    };
    UitkTabs.prototype.centerTab = function (tabEl) {
        var tabsType = this.props.tabsType;
        if (tabsType === UitkTabsType.NATURAL) {
            tabEl.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
    };
    UitkTabs.prototype.tabScroll = function (ev) {
        // How far the mouse has been moved
        var dx = ev.clientX - this.scrollMouse.initialMousePos;
        // Scroll the element
        if (this.tabsRef.current) {
            this.tabsRef.current.scrollLeft = this.scrollMouse.scrollLeft - dx;
        }
    };
    UitkTabs.prototype.render = function () {
        var _a, _b, _c, _d, _e, _f;
        var _g, _h, _j;
        var _k = this.getChildren(), navigationItems = _k[0], tabPaneItems = _k[1];
        var _l = this.props, backgroundColor = _l.backgroundColor, className = _l.className, centerAlign = _l.centerAlign, navigationType = _l.navigationType, tabsType = _l.tabsType, withBorder = _l.withBorder;
        var _m = this.state, isTransitioning = _m.isTransitioning, mounted = _m.mounted;
        var ulClassNames = getClassNameList(UitkTabs.baseClassName, (_a = {},
            _a["".concat(UitkTabs.baseClassName, "-").concat(tabsType)] = validatePropType(tabsType, UitkTabsType),
            _a), (_b = {},
            _b["".concat(backgroundColor)] = validatePropType(backgroundColor, UitkTabsBackGroundColorType),
            _b), (_c = {},
            _c["".concat(UitkTabs.baseClassName, "-").concat(navigationType)] = validatePropType(navigationType, UitkNavigationType),
            _c), (_d = {},
            _d["".concat(UitkTabs.baseClassName, "-with-border")] = withBorder,
            _d), (_e = {},
            _e["".concat(UitkTabs.baseClassName, "-unmounted")] = !mounted,
            _e), (_f = {},
            _f["".concat(UitkTabs.baseClassName, "-natural-center-align")] = centerAlign,
            _f), className);
        var highlightStyle;
        // If our unordered list has children elements i.e. list items
        if ((_h = (_g = this.tabsRef) === null || _g === void 0 ? void 0 : _g.current) === null || _h === void 0 ? void 0 : _h.children) {
            var ul = this.tabsRef.current;
            var listItems = ul.children[this.selectedIndex || 0];
            // Grab the list item's anchor tag
            var selectedTab = listItems.firstElementChild;
            if (selectedTab) {
                this.setSelectedElementOffset(selectedTab);
                // Define the style and position of the highlighter element
                var highlighterWidth = (_j = selectedTab.getBoundingClientRect()) === null || _j === void 0 ? void 0 : _j.width;
                var highlighterOffset = this.selectedTabOffset;
                var transformString = "translate".concat('X', "(").concat(highlighterOffset, "px) scale").concat('Y', "(").concat(isTransitioning ? '2' : '1', ") scaleX(1)");
                highlightStyle = {
                    height: '',
                    transform: transformString,
                    width: highlighterWidth ? "".concat(highlighterWidth, "px") : ''
                };
            }
        }
        return (React.createElement("div", { className: "uitk-tabs-container" },
            React.createElement("ul", { ref: this.tabsRef, className: ulClassNames, role: navigationType === UitkNavigationType.JUMPLINK ? 'list' : 'tablist', onMouseDown: this.mouseDown, onMouseUp: this.mouseUp, onMouseMove: this.mouseMove, onMouseLeave: this.mouseUp },
                navigationItems,
                React.createElement(UitkTabHighlighter, { style: highlightStyle })),
            navigationType === UitkNavigationType.DEFAULT && tabPaneItems.length ? (React.createElement("div", { className: getClassNameList('uitk-tabs-content') }, tabPaneItems)) : null));
    };
    UitkTabs.displayName = 'UitkTabs';
    UitkTabs.defaultProps = {
        backgroundColor: UitkTabsBackGroundColorType.PRIMARY,
        centerAlign: false,
        navigationType: UitkNavigationType.DEFAULT,
        tabsType: UitkTabsType.EQUAL_WIDTH,
        withBorder: true
    };
    UitkTabs.baseClassName = 'uitk-tabs';
    return UitkTabs;
}(React.Component));

export { UitkNavigationType, UitkTabs, UitkTabsBackGroundColorType, UitkTabsType };
